"use client";
import React, { useState, useRef, useEffect, useCallback } from "react";

const HeroContentToggle = ({ children }) => {
  const [showFullContent, setShowFullContent] = useState(false);
  // Start `true` so SSR reserves the Show More button's slot. After hydration,
  // measure() flips this off if the content doesn't actually overflow; the
  // button stays in the DOM with visibility:hidden so layout height never shifts.
  const [isOverflowing, setIsOverflowing] = useState(true);
  const contentRef = useRef(null);

  const measure = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    if (showFullContent) {
      setIsOverflowing(true);
    } else {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    }
  }, [showFullContent]);

  useEffect(() => {
    const id = setTimeout(() => measure(), 0);
    return () => clearTimeout(id);
  }, [measure, children]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
    };
  }, [measure]);

  useEffect(() => {
    if (showFullContent && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [showFullContent]);

  return (
    <>
      <div
        ref={contentRef}
        className={`text-center mx-auto ${
          showFullContent
            ? "max-h-[250px] overflow-y-auto [scrollbar-width:thin] [scrollbar-color:#fff_transparent] [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:bg-white [&::-webkit-scrollbar-thumb]:rounded"
            : "max-h-[100px] overflow-hidden [mask-image:linear-gradient(to_bottom,black_80%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,black_80%,transparent_100%)]"
        }`}
        id="short-content"
      >
        {children}
      </div>

      <button
        type="button"
        className="font-medium mt-[13px] cursor-pointer text-primary transition-colors duration-200 hover:underline hover:text-[var(--color-primary-light)] min-h-[44px] min-w-[44px] px-1 py-2"
        onClick={() => setShowFullContent((prev) => !prev)}
        aria-expanded={showFullContent}
        aria-controls="short-content"
        aria-hidden={!isOverflowing}
        tabIndex={isOverflowing ? 0 : -1}
        style={{ visibility: isOverflowing ? "visible" : "hidden" }}
      >
        {showFullContent ? "Show Less" : "Show More"}
      </button>
    </>
  );
};

export default HeroContentToggle;
